import {AfterViewInit, Component, inject, Input, PipeTransform, signal, ViewChild} from '@angular/core';
import {ConfigTableModel} from "../models/config-table.model";
import {ConfigModelInterface} from "../../interfaces/config-model.interface";
import {FormsModule} from "@angular/forms";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {NgClass, NgComponentOutlet, UpperCasePipe} from "@angular/common";
import {Table, TableModule} from "primeng/table";
import {ButtonModule} from "primeng/button";
import {InputTextModule} from "primeng/inputtext";
import {IconField} from "primeng/iconfield";
import {InputIcon} from "primeng/inputicon";
import {MenuModule} from "primeng/menu";
import {SplitButton} from "primeng/splitbutton";
import {CheckboxModule} from "primeng/checkbox";
import {MenuItem} from "primeng/api";
import {ConfigButton} from "../models/config-button";

@Component({
    selector: 'du-config-table',
    standalone: true,
    imports: [
        FormsModule,
        TranslatePipe,
        UpperCasePipe,
        NgClass,
        NgComponentOutlet,
        TableModule,
        ButtonModule,
        InputTextModule,
        IconField,
        InputIcon,
        MenuModule,
        SplitButton,
        CheckboxModule,
    ],
    templateUrl: './config-detail-table.component.html',
    styleUrl: './config-detail-table.component.scss'
})
export class ConfigDetailTableComponent implements AfterViewInit {
    private readonly translateService = inject(TranslateService);

    @Input() configTable!: ConfigTableModel<any>;

    hasExternalFilter = false;

    @Input() set searchInput(value: string) {
        this.hasExternalFilter = true;
        this._pendingFilter = value?.trim() ?? '';
        if (this._viewReady) {
            this._dt?.filterGlobal(this._pendingFilter, 'contains');
        }
    }

    @ViewChild('dt') private _dt?: Table;

    private _pendingFilter = '';
    private _viewReady = false;

    ownSearchInput = signal('');

    ngAfterViewInit(): void {
        this._viewReady = true;
        if (this._pendingFilter) {
            this._dt?.filterGlobal(this._pendingFilter, 'contains');
        }
    }

    isAllSelected() {
        const numSelected = this.configTable.selectionModel?.selected().length;
        const numRows = this.configTable.data.length;
        return numSelected === numRows;
    }

    toggleAllRows() {
        if (this.isAllSelected()) {
            this.configTable.selectionModel?.clear();
            return;
        }
        this.configTable.selectionModel?.select(...this.configTable.data);
    }

    checkboxLabel(row?: any): string {
        if (!row) {
            return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
        }
        return `${this.configTable.selectionModel?.isSelected(row) ? 'deselect' : 'select'} row`;
    }

    async handleClickEvent(row: ConfigModelInterface) {
        if (this.configTable.onRowClick) {
            await this.configTable.onRowClick(row);
        }
    }

    pipeValue(pipes: PipeTransform[], value: any) {
        if (value === null || value === undefined) return '';

        let pipesToFormat = [...pipes];

        return pipesToFormat.reduce((prevValue, currPipe) => {
            try {
                return currPipe.transform(String(prevValue));
            } catch (e) {
                console.error(e)
                return prevValue;
            }
        }, String(value));
    }

    applyFilter(dt: Table) {
        dt.filterGlobal(this.ownSearchInput().trim(), 'contains');
    }

    getFilterFields(): string[] {
        return this.configTable.displayedColumns.map(c => c.PropertyName);
    }

    toMenuItems(button: ConfigButton, selectionModel?: any): MenuItem[] {
        return (button.menuItems ?? []).map((item): MenuItem => ({
            label: this.translateService.instant(item.text).toUpperCase(),
            icon: item.icon ?? undefined,
            disabled: item.disabled || (item.disableIfEmpty && !selectionModel?.hasValue()),
            command: () => item.onClick(),
        }));
    }

    getMenuItems(button: ConfigButton & {_menuItemsCache?: MenuItem[]}, selectionModel?: any): MenuItem[] {
        if (!button._menuItemsCache) {
            button._menuItemsCache = this.toMenuItems(button, selectionModel);
        }
        return button._menuItemsCache;
    }
}
